import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth'; // Importando useAuth
import UpgradeButton from '../../components/UpgradeButton/UpgradeButton'; // Importando o botão de upgrade

// Componente Card de Funcionalidade de IA
interface AICardProps {
  title: string;
  description: string;
  icon: string;
  delay: number;
  onClick: () => void;
  isLocked: boolean;
  dataTour?: string; // Added for tour
}

const AICard: React.FC<AICardProps> = ({ title, description, icon, delay, onClick, isLocked, dataTour }) => {
  return (
    <motion.div
      data-tour={dataTour} // Apply tour attribute
      className={`group flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 transition-all dark:border-gray-700/50 dark:bg-gray-800/30 ${isLocked
          ? 'opacity-60 cursor-not-allowed'
          : 'hover:shadow-lg dark:hover:border-primary/50 hover-lift cursor-pointer'
        }`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, type: "spring", stiffness: 300, damping: 20 }}
      onClick={isLocked ? undefined : onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-to-br from-orange-400 to-primary text-white relative">
          <span className="material-symbols-outlined text-4xl">
            {icon}
          </span>
          {isLocked && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
              <Lock className="size-6 text-white" />
            </div>
          )}
        </div>
        {isLocked && (
          <span className="text-xs font-bold text-red-500 dark:text-red-400">{title.includes('Atendimento') ? 'PRO' : 'BASIC'}</span>
        )}
      </div>
      <div className="flex flex-col">
        <h3 className="text-xl font-bold text-[#181411] dark:text-white">{title}</h3>
        <p className="mt-1 text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      {isLocked && (
        <UpgradeButton />
      )}
    </motion.div>
  );
};


const AIPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile } = useAuth(); // Usando o perfil

  const isProUser = profile?.subscription_status === 'pro';

  const handleCardClick = (feature: string) => {
    if (feature === 'bulk-send') {
      navigate('/ia/bulk-send');
    } else if (feature === 'whatsapp-agent') {
      if (isProUser) {
        navigate('/ia/whatsapp-agent');
      }
    } else {
      console.log(`Configurar ${feature}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="p-6 lg:p-10 max-w-7xl mx-auto"
      data-tour="ai-page" // Added for tour
    >
      <div className="mb-8">
        <motion.p
          className="text-[#181411] dark:text-white text-4xl font-black leading-tight tracking-[-0.033em]"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          {t('ai.title')}
        </motion.p>
        <motion.p
          className="mt-2 text-gray-600 dark:text-gray-400"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          {t('ai.subtitle')}
        </motion.p>
      </div>

      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        <AICard
          title={t('ai.card_bulk_send_title')}
          description={t('ai.card_bulk_send_description')}
          icon="rocket_launch"
          delay={0.3}
          onClick={() => handleCardClick('bulk-send')}
          isLocked={false}
          dataTour="bulk-send-card" // Added for tour
        />
        <AICard
          title={t('ai.card_whatsapp_agent_title')}
          description={t('ai.card_whatsapp_agent_description')}
          icon="support_agent"
          delay={0.4}
          onClick={() => handleCardClick('whatsapp-agent')}
          isLocked={!isProUser}
          dataTour="whatsapp-agent-card" // Added for tour
        />
      </div>
    </motion.div>
  );
};

export default AIPage;